import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Box,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Business as BusinessIcon,
    LocalShipping as CarrierIcon,
    Factory as OutletIcon,
    Store as SupplierIcon,
    AccountBalance as BillToIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    getAllCompanies,
    createCompany,
    updateCompany,
    deleteCompany,
    checkCompanyNameExists,
    Company,
} from '../api/handleCompanies';
import { useAccount } from '../contexts/AccountContext';
import { supabase } from '../supabaseClient';
import { API_BASE_URL } from '../config/api';

const Companies = () => {
    const { currentAccount, isImpersonating, impersonatedUser } = useAccount();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCompany, setEditingCompany] = useState<Company | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [filterType, setFilterType] = useState<'all' | 'carrier' | 'outlet' | 'supplier' | 'billto'>('all');
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        fetchCompanies();
    }, [currentAccount, isImpersonating, impersonatedUser]);

    const fetchCompanies = async () => {
        if (!currentAccount) return;

        setLoading(true);
        setError(null);
        try {
            const impersonationData = isImpersonating && impersonatedUser ? {
                isImpersonating: true,
                impersonatedUserId: impersonatedUser.id,
                accountId: currentAccount.account_id,
            } : undefined;

            const companiesData = await getAllCompanies(impersonationData);
            setCompanies(companiesData);
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddCompany = () => {
        setEditingCompany(null);
        setDialogOpen(true);
    };

    const handleEditCompany = (company: Company) => {
        setEditingCompany(company);
        setDialogOpen(true);
    };

    const handleDeleteCompany = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this company?')) {
            try {
                const impersonationData = isImpersonating && impersonatedUser && currentAccount ? {
                    isImpersonating: true,
                    impersonatedUserId: impersonatedUser.id,
                    accountId: currentAccount.account_id,
                } : undefined;

                await deleteCompany(id, impersonationData);
                setCompanies(companies.filter(c => c.id !== id));
            } catch (error: any) {
                setError(error.message);
            }
        }
    };

    const validationSchema = Yup.object({
        name: Yup.string().required('Company name is required'),
        email: Yup.string().email('Invalid email format'),
        phone: Yup.string(),
        isCarrier: Yup.boolean(),
        isOutlet: Yup.boolean(),
        isSupplier: Yup.boolean(),
        isBillTo: Yup.boolean(),
    });

    const formik = useFormik({
        initialValues: {
            name: editingCompany?.name || '',
            alias: editingCompany?.alias || '',
            address: editingCompany?.address || '',
            city: editingCompany?.city || '',
            state: editingCompany?.state || '',
            zipCode: editingCompany?.zipCode || '',
            phone: editingCompany?.phone || '',
            email: editingCompany?.email || '',
            contactPerson: editingCompany?.contactPerson || '',
            isCarrier: editingCompany?.isCarrier || false,
            isOutlet: editingCompany?.isOutlet || false,
            isSupplier: editingCompany?.isSupplier || false,
            isBillTo: editingCompany?.isBillTo || false,
            notes: editingCompany?.notes || '',
            isActive: editingCompany?.isActive ?? true,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setSubmitting(true);
            try {
                if (!currentAccount) {
                    throw new Error('No account selected');
                }

                const impersonationData = isImpersonating && impersonatedUser && currentAccount ? {
                    isImpersonating: true,
                    impersonatedUserId: impersonatedUser.id,
                    accountId: currentAccount.account_id,
                } : undefined;

                if (editingCompany) {
                    const updated = await updateCompany(editingCompany.id!, values, impersonationData);
                    setCompanies(companies.map(c =>
                        c.id === editingCompany.id ? updated : c
                    ));
                } else {
                    const newCompany = await createCompany(values, currentAccount.account_id, impersonationData);
                    setCompanies([newCompany, ...companies]);
                }
                setDialogOpen(false);
                formik.resetForm();
            } catch (error: any) {
                setError(error.message);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const getCompanyTypes = (company: Company) => {
        const types = [];
        if (company.isCarrier) types.push('Carrier');
        if (company.isOutlet) types.push('Outlet');
        if (company.isSupplier) types.push('Supplier');
        if (company.isBillTo) types.push('Bill-To');
        return types;
    };

    const getFilteredCompanies = () => {
        if (filterType === 'all') return companies;
        return companies.filter(company => {
            switch (filterType) {
                case 'carrier': return company.isCarrier;
                case 'outlet': return company.isOutlet;
                case 'supplier': return company.isSupplier;
                case 'billto': return company.isBillTo;
                default: return true;
            }
        });
    };

    const filteredCompanies = getFilteredCompanies();
    const paginatedCompanies = filteredCompanies.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" component="h1" fontWeight="bold">
                    Companies
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddCompany}
                >
                    Add Company
                </Button>
            </Box>

            {/* Filter Section */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="subtitle1">Filter by type:</Typography>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Type</InputLabel>
                            <Select
                                value={filterType}
                                label="Type"
                                onChange={(e) => setFilterType(e.target.value as any)}
                            >
                                <MenuItem value="all">All Companies</MenuItem>
                                <MenuItem value="carrier">Carriers</MenuItem>
                                <MenuItem value="outlet">Outlets</MenuItem>
                                <MenuItem value="supplier">Suppliers</MenuItem>
                                <MenuItem value="billto">Bill-To</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography variant="body2" color="text.secondary">
                            Showing {filteredCompanies.length} of {companies.length} companies
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Companies Table */}
            <Card>
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">
                            Company Directory
                        </Typography>
                        {loading && <CircularProgress size={24} />}
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Company Name</TableCell>
                                    <TableCell>Contact</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell>Types</TableCell>
                                    <TableCell>Contact Person</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedCompanies.map((company) => (
                                    <TableRow key={company.id} hover>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <BusinessIcon color="primary" />
                                                <Box>
                                                    <Typography variant="subtitle2">
                                                        {company.name}
                                                    </Typography>
                                                    {company.alias && (
                                                        <Typography variant="caption" color="primary" sx={{ fontStyle: 'italic' }}>
                                                            Also known as: {company.alias}
                                                        </Typography>
                                                    )}
                                                    {company.notes && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {company.notes}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                {company.phone && (
                                                    <Typography variant="body2">{company.phone}</Typography>
                                                )}
                                                {company.email && (
                                                    <Typography variant="body2" color="primary">
                                                        {company.email}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                {company.address && (
                                                    <Typography variant="body2">{company.address}</Typography>
                                                )}
                                                {(company.city || company.state || company.zipCode) && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {[company.city, company.state, company.zipCode]
                                                            .filter(Boolean)
                                                            .join(', ')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={0.5} flexWrap="wrap">
                                                {company.isCarrier && (
                                                    <Chip
                                                        icon={<CarrierIcon />}
                                                        label="Carrier"
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                )}
                                                {company.isOutlet && (
                                                    <Chip
                                                        icon={<OutletIcon />}
                                                        label="Outlet"
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                    />
                                                )}
                                                {company.isSupplier && (
                                                    <Chip
                                                        icon={<SupplierIcon />}
                                                        label="Supplier"
                                                        size="small"
                                                        color="success"
                                                        variant="outlined"
                                                    />
                                                )}
                                                {company.isBillTo && (
                                                    <Chip
                                                        icon={<BillToIcon />}
                                                        label="Bill-To"
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {company.contactPerson || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditCompany(company)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteCompany(company.id!)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredCompanies.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                    />
                </CardContent>
            </Card>

            {/* Add/Edit Company Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {editingCompany ? 'Edit Company' : 'Add New Company'}
                </DialogTitle>
                <form onSubmit={formik.handleSubmit}>
                    <DialogContent>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Box display="flex" gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
                                <TextField
                                    fullWidth
                                    name="name"
                                    label="Company Name *"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                />
                                <TextField
                                    fullWidth
                                    name="alias"
                                    label="Alias"
                                    value={formik.values.alias}
                                    onChange={formik.handleChange}
                                    helperText="Alternative name users might call this company"
                                />
                            </Box>
                            <Box display="flex" gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
                                <TextField
                                    fullWidth
                                    name="contactPerson"
                                    label="Contact Person"
                                    value={formik.values.contactPerson}
                                    onChange={formik.handleChange}
                                />
                            </Box>
                            <TextField
                                fullWidth
                                name="address"
                                label="Address"
                                value={formik.values.address}
                                onChange={formik.handleChange}
                            />
                            <Box display="flex" gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
                                <TextField
                                    fullWidth
                                    name="city"
                                    label="City"
                                    value={formik.values.city}
                                    onChange={formik.handleChange}
                                />
                                <TextField
                                    fullWidth
                                    name="state"
                                    label="State"
                                    value={formik.values.state}
                                    onChange={formik.handleChange}
                                />
                                <TextField
                                    fullWidth
                                    name="zipCode"
                                    label="ZIP Code"
                                    value={formik.values.zipCode}
                                    onChange={formik.handleChange}
                                />
                            </Box>
                            <Box display="flex" gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
                                <TextField
                                    fullWidth
                                    name="phone"
                                    label="Phone"
                                    value={formik.values.phone}
                                    onChange={formik.handleChange}
                                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                                    helperText={formik.touched.phone && formik.errors.phone}
                                />
                                <TextField
                                    fullWidth
                                    name="email"
                                    label="Email"
                                    type="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Company Types:
                                </Typography>
                                <Box display="flex" gap={2} flexWrap="wrap">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="isCarrier"
                                                checked={formik.values.isCarrier}
                                                onChange={formik.handleChange}
                                            />
                                        }
                                        label="Carrier"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="isOutlet"
                                                checked={formik.values.isOutlet}
                                                onChange={formik.handleChange}
                                            />
                                        }
                                        label="Outlet"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="isSupplier"
                                                checked={formik.values.isSupplier}
                                                onChange={formik.handleChange}
                                            />
                                        }
                                        label="Supplier"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="isBillTo"
                                                checked={formik.values.isBillTo}
                                                onChange={formik.handleChange}
                                            />
                                        }
                                        label="Bill-To"
                                    />
                                </Box>
                            </Box>
                            <TextField
                                fullWidth
                                name="notes"
                                label="Notes"
                                multiline
                                rows={3}
                                value={formik.values.notes}
                                onChange={formik.handleChange}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="isActive"
                                        checked={formik.values.isActive}
                                        onChange={formik.handleChange}
                                    />
                                }
                                label="Active"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting}
                        >
                            {submitting ? 'Saving...' : (editingCompany ? 'Update' : 'Create')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Container>
    );
};

export default Companies; 